import React, { useMemo, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateAccount,
  useListAndSearchAllAccounts,
  CACHE_KEYS,
  AccountTypeEnum,
} from "@bn/api/apiv1";
import { Modal } from "@components/Modal";
import { Banner } from "@components/Banner";
import { DropdownWithSections } from "@components/SelectOptions/DropdownWithSections";
import { LoadingButton } from "../../progress/LoadingButton";
import { groupDepots } from "./helper";

type Props = {
  allowNewDepot?: boolean;
  selectedDepotId?: number;
  onDepotChange: (depotId: number) => void;
  types?: AccountTypeEnum[];
};

export const DepotSelect = ({
  selectedDepotId,
  allowNewDepot = true,
  onDepotChange,
  types = ["01_depot", "02_cash"],
}: Props) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [depotId, setDepotId] = useState<number>(selectedDepotId ?? 0);
  const queryClient = useQueryClient();
  const { mutate: createNewDepot, isPending: isSaving } = useCreateAccount();

  const { data: allDepots, isFetching } = useListAndSearchAllAccounts({
    type: types,
  });

  const handleDepotIdChange = (id: string | number) => {
    const numericId = typeof id === "string" ? parseInt(id, 10) : id;
    setDepotId(numericId);
    onDepotChange(numericId);
  };

  const handleSaveNewDepot = () => {
    setError(undefined);
    createNewDepot(
      {
        createOrUpdateAccountParams: {
          name: name,
          type: "01_depot",
          enableImplicitTransactions: true,
        },
      },
      {
        onSuccess: async (data) => {
          await queryClient.refetchQueries({
            queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
          });
          handleDepotIdChange(data.id);
          setIsModalOpen(false);
          setName("");
        },
        onError: (error) => {
          setError(error.message);
        },
      }
    );
  };

  const groups = useMemo(
    () => groupDepots(allDepots?.accounts || []),
    [allDepots]
  );

  return (
    <Box
      display="flex"
      justifyContent="space-evenly"
      alignItems="flex-end"
      gap={3}
    >
      <DropdownWithSections
        label={t("components.depotSelect.label")}
        defaultValue={depotId}
        onChange={handleDepotIdChange}
        sections={groups}
        isFetching={isFetching}
      />
      {allowNewDepot && (
        <Button
          variant="secondary"
          size="small"
          data-testid="new-depot"
          onClick={() => setIsModalOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          {t("transactionWizard.nameAndDepotStep.newDepot")}
        </Button>
      )}
      <Modal
        height="auto"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t("components.depotSelect.modal.title")}
      >
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            autoComplete="off"
            label={t("transactionWizard.newDepotStep.name")}
            data-testid="name"
            fullWidth
            onChange={(event) => {
              setName(event.target.value);
            }}
            type="text"
            value={name}
            slotProps={{
              htmlInput: {
                style: { textAlign: "left" },
                "data-testid": "name-input",
              },
            }}
          />
          {error && <Banner type="error" text={error} />}
          <Box display="flex" justifyContent="flex-end">
            <LoadingButton
              isFetching={isSaving}
              onClick={handleSaveNewDepot}
              disabled={!name}
            >
              {t("components.depotSelect.modal.save")}
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
